import { useState, useEffect } from 'react';
import { useMarkets } from '@/state/markets';
import { getTicker } from '@/content/tickers';
import { priceAt } from '@/engine/prices';
import { priceFmt, money, pct } from '@/lib/format';
import { cn } from '@/lib/cn';
import { sounds } from '@/audio/synth';

type OrderType = 'market' | 'limit' | 'stop' | 'trail';
type Side = 'buy' | 'sell';

export function OrderPanel() {
  const tickerId = useMarkets((s) => s.activeTickerId);
  const cash = useMarkets((s) => s.cash);
  const positions = useMarkets((s) => s.positions);
  const placeOrder = useMarkets((s) => s.placeOrder);
  const applyFill = useMarkets((s) => s.applyFill);

  const cfg = getTicker(tickerId);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  const [side, setSide] = useState<Side>('buy');
  const [type, setType] = useState<OrderType>('market');
  const [qty, setQty] = useState<string>('1');
  const [limit, setLimit] = useState<string>('');
  const [stop, setStop] = useState<string>('');
  const [trail, setTrail] = useState<string>('5');  // percent

  if (!cfg) return null;
  const livePrice = priceAt(cfg, now);
  const qtyNum = Math.max(0, parseFloat(qty) || 0);
  const limitNum = parseFloat(limit) || livePrice;
  const stopNum = parseFloat(stop) || livePrice;
  const effPrice = type === 'market' ? livePrice : type === 'limit' ? limitNum : stopNum;
  const total = qtyNum * effPrice;

  const heldQty = positions.find((p) => p.ticker === tickerId)?.qty ?? 0;
  const maxBuyQty = effPrice > 0 ? Math.floor(cash / effPrice) : 0;
  const maxSellQty = Math.floor(heldQty);

  const canBuy = side === 'buy' ? total <= cash + 0.0001 : true;
  const canSell = side === 'sell' ? qtyNum <= heldQty : true;
  const canSubmit = qtyNum > 0 && (side === 'buy' ? canBuy : canSell);

  const submit = () => {
    if (!canSubmit) {
      sounds.reject();
      return;
    }
    if (type === 'market') {
      applyFill(tickerId, side, qtyNum, livePrice);
      sounds.fill();
    } else if (type === 'limit') {
      placeOrder({ ticker: tickerId, side, type, qty: qtyNum, limitPrice: limitNum });
      sounds.notifyInfo();
    } else if (type === 'stop') {
      placeOrder({ ticker: tickerId, side, type, qty: qtyNum, stopPrice: stopNum });
      sounds.notifyInfo();
    } else {
      const trailFrac = Math.max(0.001, Math.min(0.5, (parseFloat(trail) || 5) / 100));
      placeOrder({ ticker: tickerId, side, type, qty: qtyNum, trailPct: trailFrac, trailPeak: livePrice });
      sounds.notifyInfo();
    }
  };

  // Keyboard shortcuts (per UI.md): B=buy, S=sell, Enter=submit. Skip when typing in a field.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') return;
      if (e.key.toLowerCase() === 'b') setSide('buy');
      else if (e.key.toLowerCase() === 's') setSide('sell');
      else if (e.key === 'Enter') submit();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [side, type, qty, limit, tickerId]);

  // Held-position summary for the active ticker — shows avg cost + live unrealized P&L
  // so the player has all the info they need to size the next order without flipping panels.
  const heldPos = positions.find((p) => p.ticker === tickerId);
  const heldUpnl = heldPos ? (livePrice - heldPos.avgPrice) * heldPos.qty : 0;
  const heldUpnlPct = heldPos ? livePrice / heldPos.avgPrice - 1 : 0;
  const heldPositive = heldUpnl >= 0;

  return (
    <div className="flex flex-col gap-2">
      {heldPos && (
        <div className="flex items-center justify-between gap-2 rounded border border-[var(--border-subtle)] bg-[var(--bg-deep)] px-2 py-1 text-mono-xs">
          <span className="text-[var(--text-muted)]">held {heldPos.qty.toFixed(0)} @ {priceFmt(heldPos.avgPrice)}</span>
          <span className={cn('tabular-nums', heldPositive ? 'text-[var(--positive)]' : 'text-[var(--negative)]')}>
            {money(heldUpnl)} ({pct(heldUpnlPct)})
          </span>
        </div>
      )}
      <div className="flex gap-1">
        <SideButton label="BUY" active={side === 'buy'} onClick={() => setSide('buy')} positive />
        <SideButton label="SELL" active={side === 'sell'} onClick={() => setSide('sell')} />
      </div>
      <div className="flex gap-1">
        <TypeButton label="MKT" active={type === 'market'} onClick={() => setType('market')} />
        <TypeButton label="LMT" active={type === 'limit'} onClick={() => setType('limit')} />
        <TypeButton label="STP" active={type === 'stop'} onClick={() => setType('stop')} />
        <TypeButton label="TRL" active={type === 'trail'} onClick={() => setType('trail')} />
      </div>
      <NumberField label="QTY" value={qty} onChange={setQty} />
      <div className="flex gap-1">
        {[1, 10, 100].map((n) => {
          const active = qty === n.toString();
          return (
            <button
              key={n}
              onClick={() => setQty(n.toString())}
              className={cn(
                'flex-1 rounded border px-1 py-0.5 text-mono-xs transition-colors',
                active
                  ? 'border-[var(--accent-primary)] bg-[var(--accent-primary)] text-black font-bold'
                  : 'border-[var(--border-subtle)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]',
              )}
            >
              {n}
            </button>
          );
        })}
        {/* Cash-percent presets — quick way to size by % of available cash */}
        {side === 'buy' && [25, 50].map((pctOfCash) => {
          const targetCash = cash * (pctOfCash / 100);
          const targetQty = effPrice > 0 ? Math.floor(targetCash / effPrice) : 0;
          const active = qty === targetQty.toString() && targetQty > 0;
          return (
            <button
              key={`pct${pctOfCash}`}
              onClick={() => setQty(targetQty.toString())}
              className={cn(
                'flex-1 rounded border px-1 py-0.5 text-mono-xs transition-colors',
                active
                  ? 'border-[var(--accent-primary)] bg-[var(--accent-primary)] text-black font-bold'
                  : 'border-[var(--border-subtle)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]',
              )}
              title={`${pctOfCash}% of cash = ${targetQty} shares`}
            >
              {pctOfCash}%
            </button>
          );
        })}
        {(() => {
          const maxQ = side === 'buy' ? maxBuyQty : maxSellQty;
          const active = qty === maxQ.toString() && maxQ > 0;
          return (
            <button
              onClick={() => setQty(maxQ.toString())}
              className={cn(
                'flex-1 rounded border px-1 py-0.5 text-mono-xs transition-colors',
                active
                  ? 'border-[var(--accent-primary)] bg-[var(--accent-primary)] text-black font-bold'
                  : 'border-[var(--border-subtle)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]',
              )}
              title={side === 'buy' ? `Max: ${maxBuyQty}` : `Hold: ${maxSellQty}`}
            >
              max
            </button>
          );
        })()}
      </div>
      {type === 'limit' && (
        <NumberField
          label="LIMIT"
          value={limit}
          onChange={setLimit}
          placeholder={priceFmt(livePrice)}
        />
      )}
      {type === 'stop' && (
        <>
          <NumberField
            label="STOP"
            value={stop}
            onChange={setStop}
            placeholder={priceFmt(livePrice)}
          />
          <span className="text-[10px] text-[var(--text-muted)]">
            {side === 'sell'
              ? `triggers SELL when price ≤ stop (cut your losses)`
              : `triggers BUY when price ≥ stop (chase the breakout)`}
          </span>
        </>
      )}
      {type === 'trail' && (
        <>
          <NumberField
            label="TRAIL %"
            value={trail}
            onChange={setTrail}
            placeholder="5"
          />
          <span className="text-[10px] text-[var(--text-muted)]">
            {side === 'sell'
              ? `peak ratchets up; triggers SELL on ${trail || '5'}% drawdown from peak`
              : `trough ratchets down; triggers BUY on ${trail || '5'}% rally from trough`}
          </span>
        </>
      )}
      <div className="flex items-baseline justify-between border-t border-[var(--border-subtle)] pt-2 text-mono-sm">
        <span className="text-[var(--text-muted)]">Total</span>
        <span className="tabular-nums text-[var(--text-primary)]">{money(total)}</span>
      </div>
      <button
        onClick={submit}
        disabled={!canSubmit}
        className={cn(
          'mt-1 rounded px-3 py-2 text-mono-sm font-bold transition-all',
          canSubmit
            ? side === 'buy'
              ? 'bg-[var(--positive)] text-black hover:brightness-110 active:scale-[0.98]'
              : 'bg-[var(--negative)] text-black hover:brightness-110 active:scale-[0.98]'
            : 'bg-[var(--bg-elevated)] text-[var(--text-muted)] cursor-not-allowed',
        )}
      >
        {side.toUpperCase()} {qty || '0'} {tickerId} {type === 'market' ? '@MKT' : `@${limit || '—'}`}
      </button>
      {!canSubmit && qtyNum > 0 && (
        <span className="text-mono-xs text-[var(--negative)]">
          {side === 'buy' ? 'insufficient cash' : `you only hold ${heldQty}`}
        </span>
      )}
    </div>
  );
}

function SideButton({ label, active, onClick, positive }: { label: string; active: boolean; onClick: () => void; positive?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex-1 rounded px-2 py-1.5 text-mono-sm font-bold transition-colors',
        active
          ? positive
            ? 'bg-[var(--positive)] text-black'
            : 'bg-[var(--negative)] text-black'
          : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]',
      )}
    >
      {label}
    </button>
  );
}

function TypeButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex-1 rounded px-2 py-1 text-mono-xs transition-colors',
        active
          ? 'bg-[var(--bg-elevated)] text-[var(--text-primary)]'
          : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]',
      )}
    >
      {label}
    </button>
  );
}

function NumberField({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label className="flex items-center justify-between gap-2 rounded border border-[var(--border-subtle)] bg-[var(--bg-deep)] px-2 py-1.5">
      <span className="text-mono-xs uppercase text-[var(--text-muted)]">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        min="0"
        step="any"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-20 bg-transparent text-right text-mono-sm tabular-nums text-[var(--text-primary)] outline-none"
      />
    </label>
  );
}
